import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ErrorDetails = ({ errors, onRetry, onManualOverride }) => {
  const [expandedError, setExpandedError] = useState(null);
  const [showManualMatch, setShowManualMatch] = useState(null);

  const toggleErrorExpansion = (errorId) => {
    setExpandedError(expandedError === errorId ? null : errorId);
  };

  const handleManualMatch = (errorId) => {
    setShowManualMatch(errorId);
  };

  const handleManualOverride = (errorId, overrideData) => {
    onManualOverride(errorId, overrideData);
    setShowManualMatch(null);
  };

  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="bg-surface border border-border rounded-lg">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} className="text-error" />
          <h3 className="text-lg font-medium text-text-primary">Failed Matches</h3>
          <span className="px-2 py-1 bg-error/10 text-error text-xs rounded-full">
            {errors.length} errors
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
        {errors.map((error) => (
          <div key={error.id} className="bg-background border border-error/20 rounded-lg">
            <div className="p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <Image
                    src={error.track.albumArt}
                    alt={`${error.track.title} album art`}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-text-primary truncate">
                        {error.track.title}
                      </h4>
                      <p className="text-sm text-text-secondary truncate">
                        {error.track.artist}
                      </p>
                      <p className="text-xs text-error mt-1">
                        {error.reason}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => toggleErrorExpansion(error.id)}
                      className="p-1 rounded-md hover:bg-surface-secondary transition-smooth"
                    >
                      <Icon 
                        name={expandedError === error.id ? "ChevronUp" : "ChevronDown"} 
                        size={16} 
                        className="text-text-muted"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {expandedError === error.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-text-primary mb-2">Error Details</h5>
                    <p className="text-sm text-text-secondary">
                      {error.details || "No additional details available."}
                    </p>
                  </div>

                  {error.suggestions && error.suggestions.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-text-primary mb-2">
                        Suggested Matches ({error.suggestions.length})
                      </h5>
                      <div className="space-y-2">
                        {error.suggestions.map((suggestion, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
                            <div className="flex items-center space-x-3">
                              <Image
                                src={suggestion.albumArt}
                                alt={`${suggestion.title} album art`}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                              <div>
                                <p className="text-sm font-medium text-text-primary">
                                  {suggestion.title}
                                </p>
                                <p className="text-xs text-text-secondary">
                                  {suggestion.artist} • {suggestion.matchScore}% match
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="primary"
                              size="xs"
                              onClick={() => handleManualOverride(error.id, suggestion)}
                            >
                              Use This
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => onRetry(error.id)}
                      iconName="RotateCcw"
                      iconSize={14}
                    >
                      Retry
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleManualMatch(error.id)}
                      iconName="Search"
                      iconSize={14}
                    >
                      Manual Search
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ErrorDetails;